// Apolo IDE — thin fetch wrapper with auto-Authorization header.
//
// Read the bearer token from localStorage (`apolo:auth:token`) so HTTP
// utilities can be used outside of the Zustand store context (e.g. in
// hooks executed during module init). Errors come back as `{ error, code }`.

import { apiUrl } from './apiBase.js';

// v10.6: re-export apiUrl pra compat — vários hooks/components importam
// `apiUrl` de './api.js' (sprint plan-tab + voice + mentions). A fonte
// canônica é apiBase.js, mas re-exportar aqui evita quebrar 8 callsites.
export { apiUrl };

const TOKEN_STORAGE_KEY = 'apolo:auth:token';
const REMEMBER_KEY = 'apolo:auth:remember';

// "Remember me" stores token in localStorage (survives quit). Default ON.
// When OFF, token lives in sessionStorage (gone on quit/close).
function preferredStorage() {
  if (typeof window === 'undefined') return null;
  try {
    const remember = window.localStorage?.getItem(REMEMBER_KEY);
    // Default: true. Only sessionStorage when explicitly "false".
    if (remember === 'false') return window.sessionStorage || null;
    return window.localStorage || null;
  } catch {
    return null;
  }
}

export function setRememberMe(enabled) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(REMEMBER_KEY, enabled ? 'true' : 'false');
  } catch {
    /* ignore */
  }
}

export function getRememberMe() {
  if (typeof window === 'undefined' || !window.localStorage) return true;
  try {
    return window.localStorage.getItem(REMEMBER_KEY) !== 'false';
  } catch {
    return true;
  }
}

export function getStoredToken() {
  if (typeof window === 'undefined') return null;
  try {
    // Try preferred first, fall back to the other (handles preference change).
    const local = window.localStorage?.getItem(TOKEN_STORAGE_KEY);
    const session = window.sessionStorage?.getItem(TOKEN_STORAGE_KEY);
    return local || session || null;
  } catch {
    return null;
  }
}

export function setStoredToken(token) {
  if (typeof window === 'undefined') return;
  const storage = preferredStorage();
  try {
    // Always clear both first so a stale token in the other store cannot leak.
    window.localStorage?.removeItem(TOKEN_STORAGE_KEY);
    window.sessionStorage?.removeItem(TOKEN_STORAGE_KEY);
    if (token && storage) storage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    // storage write may fail in private mode / sandboxed iframes — ignore.
  }
}

// E3-WS2-T01 — in-flight dedupe. Concurrent IDENTICAL idempotent GETs (the mount/
// remount over-fetch the AUDIT flagged: /sessions fired twice, /adapters per consumer)
// collapse into ONE network round-trip. Per-renderer Map (one OS user per Electron
// renderer) keyed by the GET URL → CANNOT serve another session/user's data (P2).
// Cleared on settle, so sequential calls re-fetch (no stale cache).
const _inFlightGets = new Map();

/**
 * apiFetch — JSON request to apolo-api → `{ ok, status, data, error? }`.
 * Concurrent identical GETs without a per-caller `signal` share one round-trip and the
 * SAME parsed result (body already consumed into `data` → no Response re-read hazard).
 * Non-GET, signal'd, or distinct-path requests bypass the dedupe.
 */
export function apiFetch(path, opts = {}) {
  const { method = 'GET', signal } = opts;
  // A shared promise can't honor one caller's abort without cancelling the others,
  // and mutations must never be collapsed — so only signal-less GETs are deduped.
  if (method !== 'GET' || signal) return apiFetchRaw(path, opts);
  const key = `GET:${apiUrl(path)}`;
  const existing = _inFlightGets.get(key);
  if (existing) return existing;
  const p = apiFetchRaw(path, opts);
  _inFlightGets.set(key, p);
  p.finally(() => { _inFlightGets.delete(key); });
  return p;
}

/**
 * Upload a multipart FormData (file upload) to apolo-api. Unlike apiFetch this does
 * NOT set Content-Type — the browser sets the multipart boundary. Returns the same
 * { ok, status, data, error } shape.
 */
export async function apiUpload(path, formData) {
  const auth = getStoredToken();
  try {
    const res = await fetch(apiUrl(path), {
      method: 'POST',
      headers: { Accept: 'application/json', ...(auth ? { Authorization: `Bearer ${auth}` } : {}) },
      body: formData,
    });
    const data = await res.json().catch(() => null);
    return { ok: res.ok, status: res.status, data, error: res.ok ? undefined : (data?.error || `upload falhou (${res.status})`) };
  } catch (e) {
    return { ok: false, status: 0, error: e?.message || 'network error' };
  }
}

/** Fetch a binary file (with auth) and return its Blob, or null on failure. */
export async function apiDownloadBlob(path) {
  const auth = getStoredToken();
  try {
    const res = await fetch(apiUrl(path), { headers: auth ? { Authorization: `Bearer ${auth}` } : {} });
    return res.ok ? res.blob() : null;
  } catch {
    return null;
  }
}

/**
 * Send a JSON request to apolo-api.
 *
 * Always returns `{ ok, status, data, error? }`. Returns `{ok:false,status:0}` on
 * network failure (no response at all).
 */
async function apiFetchRaw(path, { method = 'GET', body, headers = {}, signal, token } = {}) {
  const url = apiUrl(path);
  const auth = token ?? getStoredToken();
  const init = {
    method,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(auth ? { Authorization: `Bearer ${auth}` } : {}),
      ...headers,
    },
    signal,
  };
  if (body !== undefined) {
    init.body = typeof body === 'string' ? body : JSON.stringify(body);
  }

  let res;
  try {
    res = await fetch(url, init);
  } catch (e) {
    const msg = String(e?.message || '');
    const hint = msg === 'Failed to fetch' || msg === 'NetworkError when attempting to fetch resource.'
      ? 'API inacessível. Em dev, o Apolo usa a API deployada (workers.dev). Rode `cd apolo-api && npm run deploy` ou, para Worker local, `npm run dev` na porta 8787 com VITE_APOLO_API=http://localhost:8787.'
      : msg || 'network error';
    return { ok: false, status: 0, error: hint };
  }

  let data = null;
  const ct = res.headers.get('Content-Type') || '';
  if (ct.includes('application/json')) {
    try {
      data = await res.json();
    } catch {
      data = null;
    }
  } else if (res.status !== 204) {
    try {
      data = await res.text();
    } catch {
      data = null;
    }
  }

  if (!res.ok) {
    const errMsg = (data && typeof data === 'object' && data.error) || `HTTP ${res.status}`;
    const code = data && typeof data === 'object' ? data.code : undefined;
    // Sprint v8.3 — auto-logout em token revoked / expired / inválido.
    // Frontend antes mostrava "Token revoked" inline sem deslogar — usuário
    // ficava preso. Agora limpa storage + dispara evento global pro auth
    // store voltar pro LoginPage automaticamente.
    if (
      res.status === 401 &&
      (errMsg === 'Token revoked' ||
        errMsg === 'Token expired' ||
        errMsg === 'Token invalid' ||
        code === 'TOKEN_REVOKED' ||
        code === 'TOKEN_EXPIRED')
    ) {
      setStoredToken(null);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('apolo:auth:revoked', { detail: { reason: errMsg } }));
      }
    }
    return {
      ok: false,
      status: res.status,
      error: errMsg,
      code,
      data,
    };
  }

  return { ok: true, status: res.status, data };
}
